import { Colors } from '@oy/uicore';
import { Libraries } from '@react-google-maps/api';
import { FormSchema } from 'src/hooks';
import { translate } from 'src/utilities/translator';
import { validatorRules } from 'src/utilities/validator';
import {
  BusinessInformation,
  FormActivationData,
  Label,
  NpwpInformation,
  OptionPaymentMethods,
  OwnerInformation,
  PICInformation,
} from './types';

export const MAX_STEPS = 5;

export const OPTIONS_PAYMENT_METHODS: OptionPaymentMethods[] = [
  {
    value: 'LinkAja',
    iconName: 'linkaja',
  },
  {
    value: 'DANA',
    iconName: 'dana',
  },
  {
    value: 'ShopeePay',
    iconName: 'shopeepay',
  },
  {
    value: 'QRIS',
    iconName: 'qris',
  },
  {
    value: 'OVO',
    iconName: 'ovo',
  },
];

export const TAXABLE_OPTIONS: Label[] = [
  { labelKey: 'common_yes', value: 'true' },
  { labelKey: 'common_no', value: 'false' },
];

export const GENDER_OPTIONS: Label[] = [
  { labelKey: 'common_male', value: 'laki' },
  { labelKey: 'common_female', value: 'perempuan' },
];

export const NPWP_TYPE_OPTIONS = [
  {
    label: 'Individual dengan SKT (PPh 23)',
    value: 'Individual dengan SKT (PPh 23)',
  },
  { label: 'Individual tanpa SKT', value: 'Individual tanpa SKT' },
  {
    label: 'Local Company (PT/CV/UD/Yayasan) (PPH 23)',
    value: 'Local Company (PT/CV/UD/Yayasan) (PPH 23)',
  },
  {
    label: 'International Company',
    value: 'International Company',
  },
];

export const BUSINESS_OWNERSHIP_TYPE = [
  {
    label: 'formPaymentActivation_BusinessOwnerType_DirectOwn',
    value: 'Usaha Sendiri',
  },
  {
    label: 'formPaymentActivation_BusinessOwnerType_Franchise',
    value: 'Franchise',
  },
];

export const DEFAULT_VALUE: FormActivationData = {
  ewalletSubmitted: [],
  ewalletApplied: [],
  businessOwnershipType: '',
  ownerEmail: '',
  ownerKTP: '',
  ownerName: '',
  ownerBirthPlaceDate: '',
  ownerOccupation: '',
  ownerGender: '',
  ownerAddress: '',
  ownerPhoneNumber: '',
  ownerAddressProvince: '',
  ownerAddressCity: '',
  ownerAddressDistrict: '',
  ownerAddressSubDistrict: '',
  ownerAddressPostalCode: '',
  ownerNpwp: '',
  ownerNpwpType: '',
  npwpAddress: '',
  hasSameAddress: '',
  npwpAddressProvince: '',
  npwpAddressCity: '',
  npwpAddressDistrict: '',
  npwpAddressSubDistrict: '',
  npwpAddressPostalCode: '',
  hasSamePic: '',
  hasSameSecondPic: '',
  secondPicName: '',
  secondPicPosition: '',
  picName: '',
  picPosition: '',
  picEmail: '',
  picPhoneNumber: '',
  isTaxable: '',
  flowPayment: '',
  businessLink: '',
  businessLogo: '',
  businessPermitNumber: '',
  hasValidityPeriod: '',
  businessPlace: '',
  businessAddress: '',
  businessAddressProvince: '',
  businessAddressCity: '',
  businessAddressDistrict: '',
  businessAddressSubDistrict: '',
  businessAddressPostalCode: '',
  longitude: '',
  latitude: '',
  omsetTransactionExpectation: '',
  numberTransactionExpectation: '',
};

export enum FormField {
  EwalletSubmitted = 'ewalletSubmitted',
  EwalletApplied = 'ewalletApplied',
  Method = 'method',
  OwnerNpwp = 'ownerNpwp',
  OwnerNpwpType = 'ownerNpwpType',
  BusinessOwnershipType = 'businessOwnershipType',
  OwnerKTP = 'ownerKTP',
  OwnerName = 'ownerName',
  OwnerBirthPlace = 'ownerBirthPlace',
  OwnerBirthDate = 'ownerBirthDate',
  OwnerBirthPlaceDate = 'ownerBirthPlaceDate',
  OwnerOccupation = 'ownerOccupation',
  OwnerGender = 'ownerGender',
  OwnerAddressProvince = 'ownerAddressProvince',
  OwnerAddressCity = 'ownerAddressCity',
  OwnerAddressDistrict = 'ownerAddressDistrict',
  OwnerAddressSubDistrict = 'ownerAddressSubDistrict',
  OwnerAddressPostalCode = 'ownerAddressPostalCode',
  OwnerAddress = 'ownerAddress',
  OwnerEmail = 'ownerEmail',
  OwnerPhoneNumber = 'ownerPhoneNumber',
  NpwpAddress = 'npwpAddress',
  HasSameAddress = 'hasSameAddress',
  NpwpAddressProvince = 'npwpAddressProvince',
  NpwpAddressCity = 'npwpAddressCity',
  NpwpAddressDistrict = 'npwpAddressDistrict',
  NpwpAddressSubDistrict = 'npwpAddressSubDistrict',
  NpwpAddressPostalCode = 'npwpAddressPostalCode',
  HasSamePic = 'hasSamePic',
  HasSameSecondPic = 'hasSameSecondPic',
  SecondPicName = 'secondPicName',
  SecondPicPosition = 'secondPicPosition',
  PicName = 'picName',
  PicPosition = 'picPosition',
  PicEmail = 'picEmail',
  PicPhoneNumber = 'picPhoneNumber',
  Username = 'username',
  BrandName = 'brandName',
  IsTaxable = 'isTaxable',
  FlowPayment = 'flowPayment',
  BusinessLink = 'businessLink',
  BusinessLogo = 'businessLogo',
  BusinessPermitNumber = 'businessPermitNumber',
  HasValidityPeriod = 'hasValidityPeriod',
  BusinessPermitValidityPeriod = 'businessPermitValidityPeriod',
  BusinessPermitIssuedDate = 'businessPermitIssuedDate',
  BusinessEstablishedDate = 'businessEstablishedDate',
  BusinessPlace = 'businessPlace',
  BusinessAddress = 'businessAddress',
  BusinessAddressProvince = 'businessAddressProvince',
  BusinessAddressCity = 'businessAddressCity',
  BusinessAddressDistrict = 'businessAddressDistrict',
  BusinessAddressSubDistrict = 'businessAddressSubDistrict',
  BusinessAddressPostalCode = 'businessAddressPostalCode',
  // Longitude = 'longitude',
  // Latitude = 'latitude',
  OmsetTransactionExpectation = 'omsetTransactionExpectation',
  NumberTransactionExpectation = 'numberTransactionExpectation',
}

export const getFormSchemaMethod = (data: FormActivationData) => {
  const formSchema: FormSchema = {};

  formSchema[FormField.EwalletSubmitted] = {
    initialValue: data.ewalletSubmitted,
    validationRule: 'required',
  };

  return formSchema;
};

export const getFormSchemaOwnerInfo = (
  data: OwnerInformation,
  lang: 'id' | 'en',
  ewalletApplied?: string[],
) => {
  const formSchema: FormSchema = {};
  if (!ewalletApplied || ewalletApplied.includes('QRIS')) {
    formSchema[FormField.BusinessOwnershipType] = {
      initialValue: data.businessOwnershipType,
      validationRule: 'required',
      customErrorMessages: {
        required: translate(lang, 'formError_fieldIsRequired'),
      },
    };
  }
  formSchema[FormField.OwnerKTP] = {
    initialValue: data.ownerKTP,
    validationRule: 'required|numeric',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
      numeric: translate(
        lang,
        'formPaymentActivation_input_errorMessageNumber',
      ),
    },
  };

  formSchema[FormField.OwnerName] = {
    initialValue: data.ownerName,
    validationRule: 'required|string|max:255',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
    },
  };

  formSchema[FormField.OwnerBirthPlace] = {
    initialValue: data.ownerBirthPlace,
    validationRule: 'required|string|max:255',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
    },
  };

  formSchema[FormField.OwnerBirthDate] = {
    initialValue: data.ownerBirthDate,
    validationRule: 'required',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
    },
  };

  formSchema[FormField.OwnerOccupation] = {
    initialValue: data.ownerOccupation,
    validationRule: 'required|string|max:255',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
    },
  };

  formSchema[FormField.OwnerGender] = {
    initialValue: data.ownerGender,
    validationRule: 'required|string',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
    },
  };

  formSchema[FormField.OwnerAddressProvince] = {
    initialValue: data.ownerAddressProvince,
    validationRule: 'required|string',
    customErrorMessages: {
      required: translate(
        lang,
        'formPaymentActivation_input_errorMessageProvince',
      ),
    },
  };

  formSchema[FormField.OwnerAddressCity] = {
    initialValue: data.ownerAddressCity,
    validationRule: 'required|string',
    customErrorMessages: {
      required: translate(lang, 'formPaymentActivation_input_errorMessageCity'),
    },
  };

  formSchema[FormField.OwnerAddressDistrict] = {
    initialValue: data.ownerAddressDistrict,
    validationRule: 'required|string',
    customErrorMessages: {
      required: translate(
        lang,
        'formPaymentActivation_input_errorMessageDistrict',
      ),
    },
  };

  formSchema[FormField.OwnerAddressSubDistrict] = {
    initialValue: data.ownerAddressSubDistrict,
    validationRule: 'required|string',
    customErrorMessages: {
      required: translate(
        lang,
        'formPaymentActivation_input_errorMessageSubDistrict',
      ),
    },
  };

  formSchema[FormField.OwnerAddressPostalCode] = {
    initialValue: data.ownerAddressPostalCode,
    validationRule: validatorRules.postal_code,
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
      regex: translate(
        lang,
        'formPaymentActivation_input_errorMessagePostalCode',
      ),
    },
  };

  formSchema[FormField.OwnerAddress] = {
    initialValue: data.ownerAddress,
    validationRule: 'required|string|max:255',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
    },
  };

  formSchema[FormField.OwnerEmail] = {
    initialValue: data.ownerEmail,
    validationRule: validatorRules.email,
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
      email: translate(lang, 'formPaymentActivation_input_errorMessageEmail'),
    },
  };

  formSchema[FormField.OwnerPhoneNumber] = {
    initialValue: data.ownerPhoneNumber,
    validationRule: 'required|numeric',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
      numeric: translate(
        lang,
        'formPaymentActivation_input_errorMessageNumber',
      ),
    },
  };
  return formSchema;
};

export const getFormSchemaNpwpInfo = (
  data: NpwpInformation,
  lang: 'id' | 'en',
  ewalletApplied?: string[],
) => {
  const formSchema: FormSchema = {};

  formSchema[FormField.OwnerNpwp] = {
    initialValue: data.ownerNpwp,
    validationRule: 'required|string',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
    },
  };

  if (!ewalletApplied || ewalletApplied.includes('DANA')) {
    formSchema[FormField.OwnerNpwpType] = {
      initialValue: data.ownerNpwpType,
      validationRule: 'required|string',
      customErrorMessages: {
        required: translate(lang, 'formError_fieldIsRequired'),
      },
    };

    formSchema[FormField.NpwpAddress] = {
      initialValue: data.npwpAddress,
      validationRule: 'required|string',
      customErrorMessages: {
        required: translate(lang, 'formError_fieldIsRequired'),
      },
    };

    formSchema[FormField.HasSameAddress] = {
      initialValue: data.hasSameAddress,
      validationRule: 'string',
      customErrorMessages: {
        required: translate(lang, 'formError_fieldIsRequired'),
      },
    };

    formSchema[FormField.NpwpAddressProvince] = {
      initialValue: data.npwpAddressProvince,
      validationRule: 'required|string',
      customErrorMessages: {
        required: translate(
          lang,
          'formPaymentActivation_input_errorMessageProvince',
        ),
      },
    };

    formSchema[FormField.NpwpAddressCity] = {
      initialValue: data.npwpAddressCity,
      validationRule: 'required|string',
      customErrorMessages: {
        required: translate(
          lang,
          'formPaymentActivation_input_errorMessageCity',
        ),
      },
    };

    formSchema[FormField.NpwpAddressDistrict] = {
      initialValue: data.npwpAddressDistrict,
      validationRule: 'required|string',
      customErrorMessages: {
        required: translate(
          lang,
          'formPaymentActivation_input_errorMessageDistrict',
        ),
      },
    };

    formSchema[FormField.NpwpAddressSubDistrict] = {
      initialValue: data.npwpAddressSubDistrict,
      validationRule: 'required|string',
      customErrorMessages: {
        required: translate(
          lang,
          'formPaymentActivation_input_errorMessageSubDistrict',
        ),
      },
    };

    formSchema[FormField.NpwpAddressPostalCode] = {
      initialValue: data.npwpAddressPostalCode,
      validationRule: validatorRules.postal_code,
      customErrorMessages: {
        required: translate(lang, 'formError_fieldIsRequired'),
        regex: translate(
          lang,
          'formPaymentActivation_input_errorMessagePostalCode',
        ),
      },
    };
  }

  return formSchema;
};

export const getFormSchemaPICInfo = (
  data: PICInformation,
  lang: 'id' | 'en',
  ewalletApplied?: string[],
) => {
  const formSchema: FormSchema = {};
  if (!ewalletApplied || ewalletApplied.includes('QRIS')) {
    formSchema[FormField.SecondPicName] = {
      initialValue: data.secondPicName,
      validationRule: 'required|string|max:255',
      customErrorMessages: {
        required: translate(lang, 'formError_fieldIsRequired'),
        max: translate(
          lang,
          'formPaymentActivation_input_errorMessageSecondPicNameMax255Char',
        ),
      },
    };
    formSchema[FormField.SecondPicPosition] = {
      initialValue: data.secondPicPosition,
      validationRule: 'required|string|max:255',
      customErrorMessages: {
        required: translate(lang, 'formError_fieldIsRequired'),
        max: translate(
          lang,
          'formPaymentActivation_input_errorMessageSecondPicPositionMax255Char',
        ),
      },
    };
    formSchema[FormField.HasSamePic] = {
      initialValue: data.hasSamePic,
      validationRule: 'string',
      customErrorMessages: {
        required: translate(lang, 'formError_fieldIsRequired'),
      },
    };
    formSchema[FormField.HasSameSecondPic] = {
      initialValue: data.hasSameSecondPic,
      validationRule: 'string',
      customErrorMessages: {
        required: translate(lang, 'formError_fieldIsRequired'),
      },
    };
  }
  formSchema[FormField.PicName] = {
    initialValue: data.picName,
    validationRule: 'required|string|max:255',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
      max: translate(
        lang,
        'formPaymentActivation_input_errorMessagePicNameMax255Char',
      ),
    },
  };

  formSchema[FormField.PicPosition] = {
    initialValue: data.picPosition,
    validationRule: 'required|string|max:255',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
      max: translate(
        lang,
        'formPaymentActivation_input_errorMessagePicPositionMax255Char',
      ),
    },
  };

  formSchema[FormField.PicEmail] = {
    initialValue: data.picEmail,
    validationRule: validatorRules.email,
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
      email: translate(lang, 'formPaymentActivation_input_errorMessageEmail'),
      max: translate(
        lang,
        'formPaymentActivation_input_errorMessagePicEmailMax255Char',
      ),
    },
  };

  formSchema[FormField.PicPhoneNumber] = {
    initialValue: data.picPhoneNumber,
    validationRule: 'required|string',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
    },
  };

  return formSchema;
};

export const getFormSchemaBusinessInfo = (
  data: BusinessInformation,
  lang: 'id' | 'en',
  ewalletApplied?: string[],
  isIndividual?: boolean,
) => {
  const formSchema: FormSchema = {};

  if (!ewalletApplied || ewalletApplied.includes('LinkAja')) {
    formSchema[FormField.IsTaxable] = {
      initialValue: data.isTaxable,
      validationRule: 'required|string',
      customErrorMessages: {
        required: translate(lang, 'formError_fieldIsRequired'),
      },
    };
  }

  formSchema[FormField.BusinessLink] = {
    initialValue: data.businessLink,
    validationRule: 'required|string|max:255',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
    },
  };

  formSchema[FormField.BusinessLogo] = {
    initialValue: data.businessLogo,
    validationRule: 'required|string|max:1024',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
    },
  };

  formSchema[FormField.FlowPayment] = {
    initialValue: data.flowPayment,
    validationRule: 'required|string|max:255',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
      max: 'Maksimal 255 karakter',
    },
  };

  formSchema[FormField.BusinessEstablishedDate] = {
    initialValue: data.businessEstablishedDate,
    validationRule: `required`,
    customErrorMessages: {
      required_if: translate(lang, 'formError_fieldIsRequired'),
    },
  };

  formSchema[FormField.BusinessPlace] = {
    initialValue: data.businessPlace,
    validationRule: 'required|string|max:255',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
    },
  };

  if (!isIndividual) {
    formSchema[FormField.BusinessPermitNumber] = {
      initialValue: data.businessPermitNumber,
      validationRule: 'required|string|max:255',
      customErrorMessages: {
        required: translate(lang, 'formError_fieldIsRequired'),
      },
    };
  }

  if (!isIndividual && ewalletApplied?.includes('OVO')) {
    formSchema[FormField.HasValidityPeriod] = {
      initialValue: data.hasValidityPeriod,
      validationRule: 'required|string',
      customErrorMessages: {
        required: translate(lang, 'formError_fieldIsRequired'),
      },
    };

    formSchema[FormField.BusinessPermitValidityPeriod] = {
      initialValue: data.businessPermitValidityPeriod,
      validationRule: `required_if:${FormField.HasValidityPeriod},true`,
      customErrorMessages: {
        required_if: translate(lang, 'formError_fieldIsRequired'),
      },
    };

    formSchema[FormField.BusinessPermitIssuedDate] = {
      initialValue: data.businessPermitIssuedDate,
      validationRule: `required_if:${FormField.HasValidityPeriod},false`,
      customErrorMessages: {
        required_if: translate(lang, 'formError_fieldIsRequired'),
      },
    };

    formSchema[FormField.BusinessAddress] = {
      initialValue: data.businessAddress,
      validationRule: 'required|string',
      customErrorMessages: {
        required: translate(lang, 'formError_fieldIsRequired'),
      },
    };

    formSchema[FormField.BusinessAddressProvince] = {
      initialValue: data.businessAddressProvince,
      validationRule: 'required|string',
      customErrorMessages: {
        required: translate(
          lang,
          'formPaymentActivation_input_errorMessageProvince',
        ),
      },
    };

    formSchema[FormField.BusinessAddressCity] = {
      initialValue: data.businessAddressCity,
      validationRule: 'required|string',
      customErrorMessages: {
        required: translate(
          lang,
          'formPaymentActivation_input_errorMessageCity',
        ),
      },
    };

    formSchema[FormField.BusinessAddressDistrict] = {
      initialValue: data.businessAddressDistrict,
      validationRule: 'required|string',
      customErrorMessages: {
        required: translate(
          lang,
          'formPaymentActivation_input_errorMessageDistrict',
        ),
      },
    };

    formSchema[FormField.BusinessAddressSubDistrict] = {
      initialValue: data.businessAddressSubDistrict,
      validationRule: 'required|string',
      customErrorMessages: {
        required: translate(
          lang,
          'formPaymentActivation_input_errorMessageSubDistrict',
        ),
      },
    };

    formSchema[FormField.BusinessAddressPostalCode] = {
      initialValue: data.businessAddressPostalCode,
      validationRule: validatorRules.postal_code,
      customErrorMessages: {
        required: translate(lang, 'formError_fieldIsRequired'),
        regex: translate(
          lang,
          'formPaymentActivation_input_errorMessagePostalCode',
        ),
      },
    };
  }

  // if (ewalletApplied?.includes('OVO')) {
  //   formSchema[FormField.Longitude] = {
  //     initialValue: data.longitude,
  //     validationRule: 'required|string',
  //     customErrorMessages: {
  //       required: translate(
  //         lang,
  //         'formError_fieldIsRequiredLocation',
  //       ),
  //     },
  //   };

  //   formSchema[FormField.Latitude] = {
  //     initialValue: data.latitude,
  //     validationRule: 'required|string',
  //     customErrorMessages: {
  //       required: translate(
  //         lang,
  //         'formError_fieldIsRequiredLocation',
  //       ),
  //     },
  //   };
  // }

  formSchema[FormField.OmsetTransactionExpectation] = {
    initialValue: data.omsetTransactionExpectation,
    validationRule: 'required|string|max:255',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
      regex: translate(lang, 'formPaymentActivation_input_errorMessageNumber'),
    },
  };

  formSchema[FormField.NumberTransactionExpectation] = {
    initialValue: data.numberTransactionExpectation,
    validationRule: 'required|numeric',
    customErrorMessages: {
      required: translate(lang, 'formError_fieldIsRequired'),
      numeric: translate(
        lang,
        'formPaymentActivation_input_errorMessageNumber',
      ),
    },
  };

  return formSchema;
};

export const getFormSchema = (
  data: FormActivationData,
  lang: 'id' | 'en',
  ewalletApplied: string[],
) => {
  const formSchema: FormSchema = {
    ...getFormSchemaMethod(data),
    ...getFormSchemaOwnerInfo(data, lang),
    ...getFormSchemaPICInfo(data, lang),
    ...getFormSchemaBusinessInfo(data, lang, ewalletApplied),
  };

  return formSchema;
};

export const titleFormLayout = (step: number, lang: 'en' | 'id') => {
  switch (step) {
    case 1:
      return translate(lang, 'formPaymentActivation_choosePaymentMethod_title');
    case 2:
      return translate(lang, 'formPaymentActivation_ownerInfo_title');
    case 3:
      return translate(lang, 'formPaymentActivation_npwpInfo_title');
    case 4:
      return translate(lang, 'formPaymentActivation_picInfo_title');
    case 5:
      return translate(lang, 'common_businessInformation');
    default:
      return '';
  }
};

export const TEXT = {
  label: {
    [FormField.BusinessOwnershipType]:
      'formPaymentActivation_input_businessOwnershipLabel',
    [FormField.OwnerNpwpType]: 'formPaymentActivation_input_ownerNPWPTypeLabel',
    [FormField.OwnerNpwp]: 'common_npwpNumber',
    [FormField.OwnerKTP]: 'formPaymentActivation_input_ownerKTPLabel',
    [FormField.OwnerName]: 'formPaymentActivation_input_ownerNameLabel',
    [FormField.OwnerBirthPlaceDate]:
      'formPaymentActivation_input_ownerBirthDatePlaceLabel',
    [FormField.OwnerOccupation]:
      'formPaymentActivation_input_ownerOccupationLabel',
    [FormField.OwnerGender]: 'formPaymentActivation_input_ownerGenderLabel',
    [FormField.OwnerAddressProvince]:
      'formPaymentActivation_input_ownerAddressProvinceLabel',
    [FormField.OwnerAddressCity]:
      'formPaymentActivation_input_ownerAddressCityLabel',
    [FormField.OwnerAddressDistrict]:
      'formPaymentActivation_input_ownerAddressDistrictLabel',
    [FormField.OwnerAddressSubDistrict]:
      'formPaymentActivation_input_ownerAddressSubDistrictLabel',
    [FormField.OwnerAddressPostalCode]:
      'formPaymentActivation_input_ownerAddressPostalCodeLabel',
    [FormField.OwnerAddress]: 'formPaymentActivation_input_ownerAddressLabel',
    [FormField.OwnerEmail]: 'formPaymentActivation_input_ownerEmailLabel',
    [FormField.OwnerPhoneNumber]:
      'formPaymentActivation_input_ownerPhoneNumberLabel',
    [FormField.NpwpAddressProvince]:
      'formPaymentActivation_input_npwpAddressProvinceLabel',
    [FormField.NpwpAddressCity]:
      'formPaymentActivation_input_npwpAddressCityLabel',
    [FormField.NpwpAddressDistrict]:
      'formPaymentActivation_input_npwpAddressDistrictLabel',
    [FormField.NpwpAddressSubDistrict]:
      'formPaymentActivation_input_npwpAddressSubDistrictLabel',
    [FormField.NpwpAddressPostalCode]:
      'formPaymentActivation_input_npwpAddressPostalCodeLabel',
    [FormField.NpwpAddress]: 'formPaymentActivation_input_npwpAddressLabel',
    [FormField.SecondPicName]:
      'formPaymentActivation_input_picDirectorNameLabel',
    [FormField.SecondPicPosition]:
      'formPaymentActivation_input_picDirectorPositionLabel',
    [FormField.HasSamePic]: 'formPaymentActivation_input_hasSamePicLabel',
    [FormField.PicName]: 'formPaymentActivation_input_picNameLabel',
    [FormField.PicPosition]: 'formPaymentActivation_input_picPositionLabel',
    [FormField.PicEmail]: 'formPaymentActivation_input_picEmailLabel',
    [FormField.PicPhoneNumber]:
      'formPaymentActivation_input_picPhoneNumberLabel',
    [FormField.Username]: 'common_username',
    [FormField.BrandName]: 'common_brandName',
    [FormField.IsTaxable]: 'formPaymentActivation_input_isTaxableLabel',
    [FormField.FlowPayment]: 'formPaymentActivation_input_flowPaymentLabel',
    [FormField.BusinessLink]: 'formPaymentActivation_input_businessLinkLabel',
    [FormField.BusinessLogo]: 'formPaymentActivation_input_businessLogoLabel',
    [FormField.BusinessPermitNumber]:
      'formPaymentActivation_input_businessPermitNumberLabel',
    [FormField.HasValidityPeriod]:
      'formPaymentActivation_input_hasValidityPeriodLabel',
    [FormField.BusinessPermitValidityPeriod]:
      'formPaymentActivation_input_businessPermitValidityPeriodLabel',
    [FormField.BusinessPermitIssuedDate]:
      'formPaymentActivation_input_businessPermitIssuedDateLabel',
    [FormField.BusinessEstablishedDate]:
      'formPaymentActivation_input_businessEstablishedDateLabel',
    [FormField.BusinessPlace]: 'formPaymentActivation_input_businessPlaceLabel',
    [FormField.BusinessAddress]:
      'formPaymentActivation_input_businessAddressLabel',
    [FormField.BusinessAddressProvince]:
      'formPaymentActivation_input_businessAddressProvinceLabel',
    [FormField.BusinessAddressCity]:
      'formPaymentActivation_input_businessAddressCityLabel',
    [FormField.BusinessAddressDistrict]:
      'formActivationBcaVa_businessAddressDistrict_label',
    [FormField.BusinessAddressSubDistrict]:
      'formPaymentActivation_input_businessAddressSubDistrictLabel',
    [FormField.BusinessAddressPostalCode]:
      'formPaymentActivation_input_businessAddressPostalCodeLabel',
    // [FormField.Longitude]:
    //   'formPaymentActivation_input_businessMapLocationLabel',
    // [FormField.Latitude]:
    //   'formPaymentActivation_input_businessMapLocationLabel',
    [FormField.OmsetTransactionExpectation]:
      'formPaymentActivation_input_omsetTransactionExpectationLabel',
    [FormField.NumberTransactionExpectation]:
      'formPaymentActivation_input_numberTransactionExpectationLabel',
  },
  placeholder: {
    [FormField.BusinessOwnershipType]:
      'formPaymentActivation_input_businessOwnershipPlaceholder',
    [FormField.OwnerNpwpType]:
      'formPaymentActivation_input_ownerNPWPTypePlaceholder',
    [FormField.OwnerNpwp]: 'formPaymentActivation_input_ownerNPWPPlaceholder',
    [FormField.OwnerKTP]: 'formPaymentActivation_input_ownerKTPPlaceholder',
    [FormField.OwnerName]: 'formPaymentActivation_input_ownerNamePlaceholder',
    [FormField.OwnerBirthPlace]:
      'formPaymentActivation_input_ownerBirthPlacePlaceholder',
    [FormField.OwnerBirthDate]:
      'formPaymentActivation_input_ownerBirthDatePlaceholder',
    [FormField.OwnerOccupation]:
      'formPaymentActivation_input_ownerOccupationPlaceholder',
    [FormField.OwnerAddressProvince]:
      'formPaymentActivation_input_ownerAddressProvincePlaceholder',
    [FormField.OwnerAddressCity]:
      'formPaymentActivation_input_ownerAddressCityPlaceholder',
    [FormField.OwnerAddressDistrict]:
      'formPaymentActivation_input_ownerAddressDistrictPlaceholder',
    [FormField.OwnerAddressSubDistrict]:
      'formPaymentActivation_input_ownerAddressSubDistrictPlaceholder',
    [FormField.OwnerAddressPostalCode]:
      'formPaymentActivation_input_ownerAddressPostalCodePlaceholder',
    [FormField.OwnerAddress]:
      'formPaymentActivation_input_ownerAddressPlaceholder',
    [FormField.OwnerEmail]: 'formPaymentActivation_input_ownerEmailPlaceholder',
    [FormField.OwnerPhoneNumber]:
      'formPaymentActivation_input_ownerPhoneNumberPlaceholder',
    [FormField.NpwpAddressProvince]:
      'formPaymentActivation_input_npwpAddressProvincePlaceholder',
    [FormField.NpwpAddressCity]:
      'formPaymentActivation_input_npwpAddressCityPlaceholder',
    [FormField.NpwpAddressDistrict]:
      'formPaymentActivation_input_npwpAddressDistrictPlaceholder',
    [FormField.NpwpAddressSubDistrict]:
      'formPaymentActivation_input_npwpAddressSubDistrictPlaceholder',
    [FormField.NpwpAddressPostalCode]:
      'formPaymentActivation_input_npwpAddressPostalCodePlaceholder',
    [FormField.NpwpAddress]:
      'formPaymentActivation_input_npwpAddressPlaceholder',
    [FormField.SecondPicName]:
      'formPaymentActivation_input_picDirectorNamePlaceholder',
    [FormField.SecondPicPosition]:
      'formPaymentActivation_input_picDirectorPositionPlaceholder',
    [FormField.PicName]: 'formPaymentActivation_input_picNamePlaceholder',
    [FormField.PicPosition]:
      'formPaymentActivation_input_picPositionPlaceholder',
    [FormField.PicEmail]: 'formPaymentActivation_input_picEmailPlaceholder',
    [FormField.PicPhoneNumber]:
      'formPaymentActivation_input_picPhoneNumberPlaceholder',
    [FormField.Username]: 'common_username',
    [FormField.BrandName]: 'common_brandName',
    [FormField.FlowPayment]:
      'formPaymentActivation_input_flowPaymentPlaceholder',
    [FormField.BusinessLink]:
      'formPaymentActivation_input_businessLinkPlaceholder',
    [FormField.BusinessLogo]:
      'formPaymentActivation_input_businessLogoPlaceholder',
    [FormField.BusinessPermitNumber]:
      'formPaymentActivation_input_businessPermitNumberPlaceholder',
    [FormField.HasValidityPeriod]:
      'formPaymentActivation_input_hasValidityPeriodPlaceholder',
    [FormField.BusinessPermitValidityPeriod]:
      'formPaymentActivation_input_businessPermitValidityPeriodPlaceholder',
    [FormField.BusinessPermitIssuedDate]:
      'formPaymentActivation_input_businessPermitIssuedDatePlaceholder',
    [FormField.BusinessEstablishedDate]:
      'formPaymentActivation_input_businessEstablishedDatePlaceholder',
    [FormField.BusinessPlace]:
      'formPaymentActivation_input_businessPlacePlaceholder',
    [FormField.BusinessAddress]:
      'formPaymentActivation_input_businessAddressPlaceholder',
    [FormField.BusinessAddressProvince]:
      'formPaymentActivation_input_businessAddressProvincePlaceholder',
    [FormField.BusinessAddressCity]:
      'formPaymentActivation_input_businessAddressCityPlaceholder',
    [FormField.BusinessAddressDistrict]:
      'formPaymentActivation_input_businessAddressDistrictPlaceholder',
    [FormField.BusinessAddressSubDistrict]:
      'formPaymentActivation_input_businessAddressSubDistrictPlaceholder',
    [FormField.BusinessAddressPostalCode]:
      'formPaymentActivation_input_businessAddressPostalCodePlaceholder',
    // [FormField.Longitude]:
    //   'formPaymentActivation_input_businessMapLocationPlaceholder',
    // [FormField.Latitude]:
    //   'formPaymentActivation_input_businessMapLocationPlaceholder',
    [FormField.OmsetTransactionExpectation]:
      'formPaymentActivation_input_numberTransactionExpectationPlaceholder',
    [FormField.NumberTransactionExpectation]:
      'formPaymentActivation_input_numberTransactionExpectationPlaceholder',
  },
  helpText: {
    [FormField.OwnerNpwp]: 'formPaymentActivation_input_ownerNPWPHelpText',
    [FormField.BusinessLogo]:
      'formPaymentActivation_input_businessLogoHelpText',
    [FormField.BusinessLink]:
      'formPaymentActivation_input_businessLinkHelpText',
    // [FormField.Longitude]:
    //   'formPaymentActivation_input_businessMapLocationHelpText',
    // [FormField.Latitude]:
    //   'formPaymentActivation_input_businessMapLocationHelpText',
    [FormField.FlowPayment]: 'formPaymentActivation_input_flowPaymentHelpText',
  },
};

export const MAP_ICON: {
  path: string;
  fillColor: string;
  fillOpacity: number;
  scale: number;
  strokeWeight: number;
} = {
  path: 'M 12 11 C 12.8284 11 13.5 10.3284 13.5 9.5 C 13.5 8.6716 12.8284 8 12 8 C 11.1716 8 10.5 8.6716 10.5 9.5 C 10.5 10.3284 11.1716 11 12 11 Z M 12 2 C 9.8921 1.9999 7.8693 2.8318 6.3712 4.3148 C 4.8732 5.7978 4.0211 7.8122 4 9.92 C 4 15.4 11.05 21.5 11.35 21.76 C 11.5311 21.9149 11.7616 22.0001 12 22.0001 C 12.2384 22.0001 12.4689 21.9149 12.65 21.76 C 13 21.5 20 15.4 20 9.92 C 19.9789 7.8122 19.1268 5.7978 17.6288 4.3148 C 16.1307 2.8318 14.1079 1.9999 12 2 Z M 12 13 C 11.3078 13 10.6311 12.7947 10.0555 12.4101 C 9.4799 12.0256 9.0313 11.4789 8.7664 10.8394 C 8.5015 10.1999 8.4322 9.4961 8.5672 8.8172 C 8.7023 8.1382 9.0356 7.5146 9.5251 7.0251 C 10.0146 6.5356 10.6382 6.2023 11.3172 6.0672 C 11.9961 5.9322 12.6999 6.0015 13.3394 6.2664 C 13.9789 6.5313 14.5256 6.9799 14.9101 7.5555 C 15.2947 8.1311 15.5 8.8078 15.5 9.5 C 15.5 10.4283 15.1313 11.3185 14.4749 11.9749 C 13.8185 12.6313 12.9283 13 12 13 Z',
  fillColor: Colors.BLUE200,
  fillOpacity: 1,
  scale: 2,
  strokeWeight: 0,
};

export const MAP_LIBRARIES: Libraries = ['places'];

export const MAP_INPUT_STYLES = {
  border: '1px solid #D2D5D7',
  width: '100%',
  height: '36px',
  padding: '0 12px',
  borderRadius: '6px',
  outline: 'none',
  textOverflow: 'ellipses',
};

export const MAP_STYLES = {
  width: '100%',
  height: '400px',
};

export const MAP_INIT_CENTER = {
  lat: -6.2,
  lng: 106.816,
};
